/**
 * The Insights page's tabs (AIC-148) and the filters each one supports. Filters live in the URL query string
 * (/insights/<tab>?cycle=…&team=…), so a view can be bookmarked or shared. All known selections travel between tabs;
 * only filters supported by the current tab reach its endpoint.
 */
export type FilterKey = "cycle" | "team" | "analyst" | "survey_type" | "from" | "to" | "build" | "modeler" | "market" | "min_delta" | "group" | "period";
export type TabId = "funnel" | "quality" | "outcomes" | "digest" | "workflow" | "cost" | "pacing" | "verifier";

export interface TabDef {
  id: TabId;
  label: string;
  filters: readonly FilterKey[];
  /** The cycle filter offers "All cycles" (every cycle except development ones). */
  allCycles: boolean;
  /** GET /api/portal/insights/<id>.csv exists (audited `insights:export`). */
  csv: boolean;
}

export const TABS: readonly TabDef[] = [
  { id: "funnel", label: "Vendor funnel", filters: ["cycle", "team", "analyst", "survey_type", "build"], allCycles: true, csv: false },
  { id: "pacing", label: "Pacing", filters: ["cycle"], allCycles: false, csv: false },
  { id: "workflow", label: "Workflow", filters: ["cycle", "team", "analyst", "survey_type"], allCycles: true, csv: false },
  { id: "quality", label: "Call quality", filters: ["cycle", "team", "analyst"], allCycles: true, csv: false },
  { id: "outcomes", label: "Data outcomes", filters: ["cycle", "team", "analyst", "survey_type", "build"], allCycles: true, csv: true },
  { id: "digest", label: "Modeler digest", filters: ["cycle", "team", "analyst", "build", "modeler", "market", "min_delta", "group", "period"], allCycles: true, csv: true },
  { id: "cost", label: "Cost", filters: ["cycle", "team", "analyst", "survey_type", "from", "to"], allCycles: true, csv: true },
  { id: "verifier", label: "Verifier accuracy", filters: ["cycle", "team", "analyst", "survey_type"], allCycles: true, csv: false },
];

/** The one switch for which tabs show in the Insights nav (every tab is built; a new tab adds its id here). */
export const INSIGHTS_TABS_ON: readonly TabId[] = ["funnel", "pacing", "workflow", "quality", "outcomes", "digest", "cost", "verifier"];

export const enabledTabs = (on: readonly TabId[] = INSIGHTS_TABS_ON): TabDef[] => TABS.filter((t) => on.includes(t.id));

export type Filters = Partial<Record<FilterKey, string>>;
export type InsightsContext = Filters & { unit?: string };
const FILTER_KEYS: readonly FilterKey[] = ["cycle", "team", "analyst", "survey_type", "from", "to", "build", "modeler", "market", "min_delta", "group", "period"];
export const FILTER_LABELS: Record<FilterKey, string> = {
  cycle: "Cycle", team: "Team", analyst: "Analyst", survey_type: "Survey type",
  from: "From", to: "To", build: "Build-mode rows", modeler: "Modeler", market: "Functional market", min_delta: "Minimum |% delta|", group: "Group by", period: "Revenue period",
};

/** Navigation context only: never pass this unfiltered object to an endpoint. */
export function readContext(query: URLSearchParams): InsightsContext {
  return { ...Object.fromEntries(FILTER_KEYS.flatMap((k) => query.get(k) ? [[k, query.get(k)!]] : [])), ...(query.get("unit") === "assignment" ? { unit: "assignment" } : {}) };
}

/** The tab's filters from a query string; unsupported and empty parameters are dropped. */
export function readFilters(query: URLSearchParams, tab: TabDef): Filters {
  const out: Filters = {};
  for (const k of tab.filters) {
    const v = query.get(k);
    if (v && !(k === "cycle" && v === "all" && !tab.allCycles)) out[k] = v;
  }
  return out;
}

/** Retain inactive selections for other tabs; readFilters controls request scope. */
export function hrefFor(tab: TabDef, filters: InsightsContext): string {
  const q = new URLSearchParams();
  for (const k of FILTER_KEYS) {
    const v = filters[k];
    if (v) q.set(k, v);
  }
  if (filters.unit === "assignment") q.set("unit", "assignment");
  const s = q.toString();
  return `/insights/${tab.id}${s ? `?${s}` : ""}`;
}
